import React from 'react';
import { useSelector } from 'react-redux';
import { Button, Grid, Typography } from '@mui/material';
import {
	isLastProductForShopper,
	areAllProductsSelectedForCurrentShopper,
	areProductsSelectedForCurrentShopper,
} from '../utils';
import { OrderProduct, SetProductWithParentId, RentalState, Shopper } from 'common/types';
import { useTranslation } from 'services/localization/useTranslation';
import * as RentalViewSelectors from 'selectors/RentalViewSelectors';
import { LockedButton } from 'components/features/FeatureButton';
import { usePermissions } from 'hooks/usePermissions';

interface Props {
	shopper: Shopper;
	shopperProducts: OrderProduct[];
	selectedNonSetProductIds: string[];
	selectedSetProducts: SetProductWithParentId[];
	rentalState: RentalState;
	hasDelivery: boolean;
	editingShopper: boolean;
	handleDeleteShopper: () => void;
	handleDeleteProducts: () => void;
	handleReturnProducts: () => void;
	handleAddProducts: () => void;
}

export const ProductActions = (props: Props) => {
	const {
		shopperProducts,
		selectedNonSetProductIds,
		selectedSetProducts,
		rentalState,
		hasDelivery,
		handleDeleteShopper,
		handleDeleteProducts,
		handleReturnProducts,
		handleAddProducts,
		editingShopper,
	} = props;

	const { t } = useTranslation();
	const { hasPermission } = usePermissions();
	const orderShoppers = useSelector(RentalViewSelectors.orderShoppers);
	const canAddRentalProductsToOrder = useSelector(RentalViewSelectors.canAddRentalProductsToOrder);

	const onlyOneShopper = orderShoppers.length === 1;

	const deletingAllProductsForShopper =
		isLastProductForShopper(shopperProducts) ||
		areAllProductsSelectedForCurrentShopper(
			shopperProducts,
			selectedNonSetProductIds,
			selectedSetProducts,
		);

	const shouldDeleteActionBeDisplayed = () =>
		areProductsSelectedForCurrentShopper(
			shopperProducts,
			selectedNonSetProductIds,
			selectedSetProducts,
		) && rentalState === 'BOOKED';

	const shouldReturnActionBeDisplayed = () =>
		areProductsSelectedForCurrentShopper(
			shopperProducts,
			selectedNonSetProductIds,
			selectedSetProducts,
		);

	const whichActionShouldBeRendered = () => {
		if (shouldDeleteActionBeDisplayed()) {
			return 'delete';
		} else if (shouldReturnActionBeDisplayed()) {
			return 'return';
		} else {
			return 'add';
		}
	};

	const renderFeedbackForTryingToDeleteLastProduct = () => {
		return shouldDeleteActionBeDisplayed() && deletingAllProductsForShopper && onlyOneShopper ? (
			<Grid item>
				<Typography color="error" variant="caption">
					{t(
						'shopperProducts.deletingProductsNotPossible',
						'All products cannot be deleted. Add a new product before deleting.',
					)}
				</Typography>
			</Grid>
		) : null;
	};

	const renderDeleteAction = () => {
		return (
			<Button
				color="primary"
				variant="contained"
				disabled={
					(deletingAllProductsForShopper && onlyOneShopper) || !hasPermission('edit_booking')
				}
				onKeyPress={(event) => {
					event.preventDefault();
				}}
				onClick={() =>
					deletingAllProductsForShopper ? handleDeleteShopper() : handleDeleteProducts()
				}
			>
				{t('common:actions.delete')}
			</Button>
		);
	};

	const renderReturnAction = () => {
		return (
			<Button
				color="primary"
				variant="contained"
				onKeyPress={(event) => {
					event.preventDefault();
				}}
				onClick={() => handleReturnProducts()}
			>
				{t('shopperProducts.returnItem', 'Return')}
			</Button>
		);
	};

	const renderAddAction = () => {
		return !canAddRentalProductsToOrder ? (
			<LockedButton
				color="primary"
				variant="outlined"
				tooltipText={t(
					'shopperProducts.addRentalProductsDisabledHelper',
					'Cannot be added to a sales order',
				)}
			>
				{t('shopperProducts.addRentalProducts', 'Add rental products')}
			</LockedButton>
		) : (
			<Button
				color="primary"
				variant="outlined"
				disabled={rentalState === 'COMPLETED' || editingShopper || !hasPermission('edit_booking')}
				onKeyPress={(event) => {
					event.preventDefault();
				}}
				onClick={handleAddProducts}
				data-qa="addProducts"
			>
				{t('shopperProducts.addRentalProducts', 'Add rental products')}
			</Button>
		);
	};
	const actionToBeRendered = whichActionShouldBeRendered();
	return (
		<React.Fragment>
			{actionToBeRendered === 'delete' ? renderDeleteAction() : null}
			{actionToBeRendered === 'return' ? renderReturnAction() : null}
			{actionToBeRendered === 'add' && !hasDelivery ? renderAddAction() : null}
			{renderFeedbackForTryingToDeleteLastProduct()}
		</React.Fragment>
	);
};

export default ProductActions;
